/* eslint-disable id-length */
import { Suspense, useEffect } from "react";
import { CachePolicies, useFetch } from "use-http";
import Spinner from "~/components/shared/Spinner";
import { LoadingChart } from "~/components/analytics/shared/LoadingStates";
import classNames from "classnames";
import Renderer, { ChartID, TimePeriod } from "~/components/ClustersInsights/Renderer";
import Icon from "~/components/shared/Icon";
import Button from "~/components/shared/Button";

enum ChartSize {
  Small = "small",
  Large = "large",
}

interface Props {
  size: ChartSize;
  id: ChartID;
  endpoint: string;
  timePeriod: TimePeriod;
  pollInterval?: number;
}

const DEFAULT_POLL_INTERVAL_SECONDS = 10;

const AsyncChart = ({
  id,
  endpoint,
  pollInterval = DEFAULT_POLL_INTERVAL_SECONDS,
  ...props
}: Props) => {
  const {
    data,
    error,
    get: fetchChartData,
  } = useFetch(endpoint, {
    suspense: true,
    cachePolicy: CachePolicies.NETWORK_ONLY,
  });

  useEffect(() => {
    fetchChartData();

    const interval = setInterval(() => {
      fetchChartData();
    }, pollInterval * 1000);

    return () => clearInterval(interval);
  }, []);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <div>There was an error loading the chart</div>
        <Button onClick={() => fetchChartData()} theme="small" className="flex gap-1">
          <Icon icon="custom/outline/16x/arrows-refresh" className="h-4 w-4" />
          <span>Retry</span>
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return <Renderer {...props} id={id} data={data} />;
};

const ChartLoader = (props: Props) => {
  return (
    <div
      className={classNames({
        "h-40": props.size === "small",
        "h-full min-h-[18rem] lg:h-[18rem]": props.size === "large",
      })}
    >
      <Suspense fallback={<LoadingState size={props.size} />}>
        <AsyncChart {...props} />
      </Suspense>
    </div>
  );
};

const LoadingState = ({ size }: { size: ChartSize }) => {
  if (size === ChartSize.Large) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingChart barWidth="25px" />
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <Spinner color={false} />
    </div>
  );
};

export default ChartLoader;
